import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";

const ModalComponent = ({
  // New modal-style props
  onSkip,
  onEnable,
  stepLabel,
  heading,
  subtitle,
  items,
  sidebarIndicators,
  sidebarBottomIcon,
  secondaryButtonText,
  primaryButtonText,
  showOverlay = false,
  // Animation & mode props
  sidebarMode = "default", // "default" | "submit" | "delete" | "warning"
  isSubmitting = false,
  showSubmitSuccess = false,
  // Legacy props (kept so existing usage does not break)
  icon,
  title,
  description,
  buttonText,
  countText,
  onButtonClick,
  buttonType = "button",
  disabled = false,
  className = "",
}) => {
  const [animationState, setAnimationState] = useState("idle");
  const [displayMode, setDisplayMode] = useState(sidebarMode);

  useEffect(() => {
    setDisplayMode(sidebarMode);
    if (isSubmitting) {
      setAnimationState("submitting");
    } else if (showSubmitSuccess) {
      setAnimationState("success");
      const timer = setTimeout(() => setAnimationState("idle"), 2000);
      return () => clearTimeout(timer);
    } else {
      setAnimationState("idle");
    }
  }, [isSubmitting, showSubmitSuccess, sidebarMode]);

  const normalizedItems = Array.isArray(items) ? items : [];
  const normalizedIndicators = Array.isArray(sidebarIndicators)
    ? sidebarIndicators
    : [false, true, false];

  const isLegacyMode =
    !heading &&
    normalizedItems.length === 0 &&
    (title || description || buttonText || countText || icon);

  // ─── Sidebar helpers ───────────────────────────────────────────────────────

  const getSidebarGradient = () => {
    switch (displayMode) {
      case "submit":  return "linear-gradient(160deg, #065f46 0%, #047857 100%)";
      case "delete":  return "linear-gradient(160deg, #ef4444 0%, #b91c1c 100%)";
      case "warning": return "linear-gradient(160deg, #f59e0b 0%, #d97706 100%)";
      default:        return "linear-gradient(160deg, #151929 0%, #0e1220 100%)";
    }
  };

  const getSidebarBorderColor = () => {
    switch (displayMode) {
      case "submit":  return "rgba(16,185,129,0.3)";
      case "delete":  return "rgba(239,68,68,0.25)";
      case "warning": return "rgba(255,255,255,0.4)";
      default:        return "rgba(99,102,241,0.25)";
    }
  };

  const getSidebarAnimation = () => {
    switch (displayMode) {
      case "submit":  return "sidebarGlowGreen 2s ease-in-out infinite";
      case "delete":  return "sidebarGlowRed 1.8s ease-in-out infinite";
      case "warning": return "sidebarGlowAmber 2s ease-in-out infinite";
      default:        return "sidebarGlowIndigo 3s ease-in-out infinite";
    }
  };

  const getIndicatorColor = (active) => {
    if (!active) return "rgba(255,255,255,0.15)";
    switch (displayMode) {
      case "submit":  return "#10b981";
      case "delete":  return "#ef4444";
      case "warning": return "#f59e0b";
      default:        return "#6366f1";
    }
  };

  const getIndicatorShadow = (active) => {
    if (!active) return "none";
    switch (displayMode) {
      case "submit":  return "0 0 10px rgba(16,185,129,0.7)";
      case "delete":  return "0 0 10px rgba(239,68,68,0.7)";
      case "warning": return "0 0 10px rgba(245,158,11,0.7)";
      default:        return "0 0 10px rgba(99,102,241,0.6)";
    }
  };

  const getPrimaryButtonBg = () => {
    if (isSubmitting) {
      return "linear-gradient(135deg, #a5b4fc 0%, #c7d2fe 100%)";
    }
    if (displayMode === "submit") {
      return "linear-gradient(135deg, #10b981 0%, #059669 100%)";
    }
    if (displayMode === "warning") {
      return "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)";
    }
    return "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)";
  };

  const getPrimaryButtonShadow = () => {
    if (isSubmitting) {
      return "0 2px 8px rgba(99,102,241,0.2)";
    }
    if (displayMode === "submit") {
      return "0 4px 14px rgba(16,185,129,0.35)";
    }
    if (displayMode === "warning") {
      return "0 4px 14px rgba(245,158,11,0.35)";
    }
    return "0 4px 14px rgba(99,102,241,0.4)";
  };

  // ─── Sidebar icons ─────────────────────────────────────────────────────────

  const DefaultIcon = () => (
    <svg
      width="22" height="22" viewBox="0 0 24 24"
      fill="none" stroke="#818cf8" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );

  const SubmitIcon = () => (
    <>
      {animationState === "submitting" && (
        <svg
          width="26" height="26" viewBox="0 0 24 24"
          fill="none" stroke="#059669" strokeWidth="2"
          style={{ animation: "spinIcon 1s linear infinite" }}
        >
          <circle cx="12" cy="12" r="1" />
          <path d="M12 2v6m0 4v6" />
          <path d="M4.22 4.22l4.24 4.24m5.08 5.08l4.24 4.24" />
          <path d="M2 12h6m4 0h6" />
          <path d="M4.22 19.78l4.24-4.24m5.08-5.08l4.24-4.24" />
        </svg>
      )}
      {animationState === "success" && (
        <svg
          width="26" height="26" viewBox="0 0 24 24"
          fill="none" stroke="#10b981" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round"
          style={{ animation: "popIn 0.6s cubic-bezier(0.34,1.56,0.64,1) forwards" }}
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
      {animationState === "idle" && (
        <svg
          width="26" height="26" viewBox="0 0 24 24"
          fill="none" stroke="#059669" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </>
  );

  const DeleteIcon = () => (
    <svg
      width="22" height="22" viewBox="0 0 24 24"
      fill="none" stroke="#ef4444" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      style={{ animation: "shake 0.5s ease-in-out 2" }}
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );

  const WarningIcon = () => (
    <svg
      width="22" height="22" viewBox="0 0 24 24"
      fill="none" stroke="#f59e0b" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      style={{ animation: "warningPulse 2s ease-in-out infinite" }}
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3.05h16.94a2 2 0 0 0 1.71-3.05L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );

  const renderSidebarIcon = () => {
    if (sidebarBottomIcon) return sidebarBottomIcon;
    switch (displayMode) {
      case "submit":  return <SubmitIcon />;
      case "delete":  return <DeleteIcon />;
      case "warning": return <WarningIcon />;
      default:        return <DefaultIcon />;
    }
  };

  // ─── Global keyframes ──────────────────────────────────────────────────────

  const keyframes = `
    @keyframes slideUp {
      from { opacity: 0; transform: translateY(28px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes sidebarEnter {
      from { opacity: 0; transform: translateX(-10px); }
      to   { opacity: 1; transform: translateX(0); }
    }
    @keyframes sidebarGlowIndigo {
      0%,100% { box-shadow: 4px 0 0px rgba(99,102,241,0); }
      50%      { box-shadow: 4px 0 28px rgba(99,102,241,0.35); }
    }
    @keyframes sidebarGlowGreen {
      0%,100% { box-shadow: 4px 0 0px rgba(16,185,129,0); }
      50%      { box-shadow: 4px 0 28px rgba(16,185,129,0.45); }
    }
    @keyframes sidebarGlowRed {
      0%,100% { box-shadow: 4px 0 0px rgba(239,68,68,0); }
      50%      { box-shadow: 4px 0 28px rgba(239,68,68,0.45); }
    }
    @keyframes sidebarGlowAmber {
      0%,100% { box-shadow: 4px 0 0px rgba(245,158,11,0); }
      50%      { box-shadow: 4px 0 28px rgba(245,158,11,0.45); }
    }
    @keyframes dotPop {
      0%   { transform: scale(0.6); opacity: 0; }
      60%  { transform: scale(1.3); }
      100% { transform: scale(1.2); opacity: 1; }
    }
    @keyframes spinIcon {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    @keyframes popIn {
      0%   { transform: scale(0.5) rotate(-45deg); opacity: 0; }
      70%  { transform: scale(1.15); }
      100% { transform: scale(1); opacity: 1; }
    }
    @keyframes shake {
      0%,100% { transform: translateX(0); }
      25%     { transform: translateX(-4px); }
      75%     { transform: translateX(4px); }
    }
    @keyframes warningPulse {
      0%,100% {
        filter: drop-shadow(0 0 0px rgba(245,158,11,0.3));
        transform: scale(1);
      }
      50% {
        filter: drop-shadow(0 0 10px rgba(245,158,11,0.7));
        transform: scale(1.08);
      }
    }
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(10px); }
      to   { opacity: 1; transform: translateY(0); }
    }
  `;

  // ─── Legacy mode ───────────────────────────────────────────────────────────

  if (isLegacyMode) {
    return (
      <div
        className={`w-full rounded-xl border border-gray-200 bg-white px-4 py-3 sm:px-5 sm:py-4 ${className}`}
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <div className="flex items-start gap-3 sm:items-center">
          <div className="flex items-center justify-center text-gray-700 bg-gray-100 h-11 w-11 shrink-0 rounded-xl">
            {icon || (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.03-9.53a.75.75 0 00-1.06-1.06L9 10.38 8.03 9.4a.75.75 0 10-1.06 1.06l1.5 1.5a.75.75 0 001.06 0l3.5-3.5z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 truncate sm:text-lg">{title}</h3>
            {description && <p className="mt-0.5 text-sm text-gray-600 sm:text-base">{description}</p>}
            {countText && <p className="mt-1 text-xs font-medium text-gray-400 sm:text-sm">{countText}</p>}
          </div>

          {buttonText && (
            <button
              type={buttonType}
              disabled={disabled}
              onClick={onButtonClick}
              className="shrink-0 rounded-full bg-emerald-100 px-4 py-1.5 text-sm font-semibold text-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {buttonText}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── Modal mode ────────────────────────────────────────────────────────────
  // Portal overlay to document.body so `position:fixed` is not trapped by
  // transformed/filtered/isolated ancestors (e.g. course cards, layout shells).

  const modalModeTree = (
    <div
      className={
        showOverlay
          ? "fixed inset-0 z-[100] flex items-center justify-center p-6"
          : "w-full"
      }
      style={showOverlay ? {
        background: "radial-gradient(circle at top, rgba(59,130,246,0.08), transparent 60%), rgba(0,0,0,0.35)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
      } : undefined}
    >
      <style>{keyframes}</style>

      <div
        className={className}
        style={{
          display: "flex",
          width: "100%",
          maxWidth: 720,
          minHeight: 360,
          borderRadius: 20,
          overflow: "hidden",
          boxShadow: "0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)",
          fontFamily: "'DM Sans', sans-serif",
          background: "#ffffff",
          animation: "slideUp 0.4s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        {/* ── SIDEBAR ── */}
        <div
          key={displayMode}
          style={{
            width: 119,
            background: getSidebarGradient(),
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "1.5rem 0",
            borderRight: `1px solid ${getSidebarBorderColor()}`,
            flexShrink: 0,
            animation: `sidebarEnter 0.35s ease, ${getSidebarAnimation()}`,
            transition: "background 0.4s ease, border-color 0.4s ease",
          }}
        >
          {/* Icon container — centred */}
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 16,
              background: "#ffffff",
              border: `1px solid ${getSidebarBorderColor()}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 16px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.8)",
              transition: "border-color 0.4s ease",
            }}
          >
            {renderSidebarIcon()}
          </div>
        </div>

        {/* ── CONTENT ── */}
        <div
          style={{
            flex: 1,
            background: "#ffffff",
            display: "flex",
            flexDirection: "column",
            padding: "2.5rem 2.5rem 2rem",
          }}
        >
          {stepLabel && (
            <div
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.08em",
                color: "#6366f1",
                textTransform: "uppercase",
                marginBottom: "0.5rem",
              }}
            >
              {stepLabel}
            </div>
          )}

          <div
            style={{
              fontSize: "1.6rem",
              fontWeight: 700,
              color: "#0f172a",
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              marginBottom: "1.2rem",
            }}
          >
            {heading}
          </div>

          {subtitle && (
            <div
              style={{
                  fontSize: "0.95rem",
                  color: "#64748b",
                  lineHeight: 1.6,
                  marginBottom: "0.95rem",
                }}
            >
              {subtitle}
            </div>
          )}

          <div
            style={{
              height: 1,
              background: "linear-gradient(90deg, #e2e8f0 0%, transparent 80%)",
              marginBottom: "1.25rem",
            }}
          />

            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.9rem" }}>
            {normalizedItems.map((item, i) => (
              <p
                key={i}
                style={{
                  margin: 0,
                  fontSize: "0.875rem",
                  color: "#475569",
                  lineHeight: 1.6,
                  animation: `fadeInUp 0.4s ease ${i * 60}ms both`,
                }}
              >
                <span style={{ fontWeight: 600, color: "#1e293b" }}>{item.title}</span>
                {item.desc && (
                  <span style={{ color: "#64748b" }}> — {item.desc}</span>
                )}
              </p>
            ))}
          </div>

          <div
            style={{
              display: "flex",
              gap: "0.8rem",
              marginTop: "1.25rem",
              paddingTop: "0.9rem",
              borderTop: "1px solid #e2e8f0",
              justifyContent: "flex-end",
            }}
          >
            {secondaryButtonText && (
              <button
                onClick={onSkip}
                type="button"
                style={{
                  padding: "0.4rem 1.1rem",
                  borderRadius: 9999,
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  background: "transparent",
                  color: "#64748b",
                  border: "1.5px solid #e2e8f0",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f8fafc";
                  e.currentTarget.style.borderColor = "#cbd5e1";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.borderColor = "#e2e8f0";
                }}
              >
                {secondaryButtonText}
              </button>
            )}

            {primaryButtonText && (
              <button
                onClick={onEnable}
                type="button"
                disabled={isSubmitting}
                style={{
                  padding: "0.4rem 1.25rem",
                  borderRadius: 9999,
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  border: "none",
                  background: isSubmitting
                    ? "linear-gradient(135deg, #6ee7b7 0%, #a7f3d0 100%)"
                    : displayMode === "submit"
                    ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                    : displayMode === "delete"
                    ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                    : getSidebarGradient(),
                  color: "#fff",
                  boxShadow: displayMode === "submit"
                    ? "0 4px 14px rgba(16,185,129,0.4)"
                    : displayMode === "delete"
                    ? "0 4px 14px rgba(220,38,38,0.35)"
                    : "0 4px 14px rgba(15,18,41,0.4)",
                  transition: "all 0.2s ease",
                  opacity: isSubmitting ? 0.7 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.filter = "brightness(1.1)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.filter = "none";
                  }
                }}
              >
                {primaryButtonText}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (showOverlay && typeof document !== "undefined") {
    return createPortal(modalModeTree, document.body);
  }

  return modalModeTree;
};

export default ModalComponent;
